import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useTheme,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import {
  initiateVPayPayment,
  createSubscription,
} from "../services/payment.service";
import { useAuth } from "../../auth/hooks/useAuth";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ShieldIcon from "@mui/icons-material/Shield";
import MemoryIcon from "@mui/icons-material/Memory";

// Animation keyframes
const slideIn = keyframes`
  0% {
    width: 36px;
    opacity: 0;
  }
  20% {
    width: 36px;
    opacity: 1;
  }
  40% {
    width: 205px;
  }
  100% {
    width: 215px;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "12px",
  border: `1px solid ${theme.palette.divider}`,
}));

const SecureBadge = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 20,
  right: 20,
  height: 36,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 18,
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.75, 1.5),
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  animation: `${slideIn} 1.5s ease-in-out forwards`,
  zIndex: 1200,
  overflow: "hidden",
  border: `1px solid ${theme.palette.grey[200]}`,
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  "& .badge-content": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    width: "fit-content",
  },
  "& .icon": {
    color: theme.palette.primary.main,
    fontSize: 18,
  },
  "& .text": {
    animation: `${fadeIn} 2s ease-in-out forwards`,
    whiteSpace: "nowrap",
    fontWeight: 500,
    fontSize: "0.813rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent 0%, ${theme.palette.background.paper} 50%, transparent 100%)`,
    opacity: 0.4,
    animation: `${fadeIn} 2s infinite`,
  },
}));

const steps = ["Plan Selection", "Payment Method", "Confirmation"];

interface PlanDetails {
  title: string;
  price: string;
  period: string;
  features: string[];
}

// Constants for localStorage keys
const PAYMENT_TRACK_ID_KEY = "subscription_payment_track_id";
const PAYMENT_PLAN_DETAILS_KEY = "subscription_payment_plan_details";

// Map plan titles to plan types
const PLAN_TYPE_MAP: Record<string, "BASIC" | "PRO" | "ENTERPRISE"> = {
  Basic: "BASIC",
  Professional: "PRO",
  Enterprise: "ENTERPRISE",
};

// Map period to billing cycle
const BILLING_CYCLE_MAP: Record<string, "MONTHLY" | "YEARLY"> = {
  month: "MONTHLY",
  year: "YEARLY",
};

const summaryFeatures = [
  "Complete POS System with Advanced Analytics",
  "Unlimited Staff & Multi-location Support",
  "Comprehensive Inventory & CRM System",
  "24/7 Priority Support & Regular Updates",
];

const SubscriptionCheckoutPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const [activeStep, setActiveStep] = useState(1);
  const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  console.log(planDetails);

  useEffect(() => {
    const state = location.state as { planDetails: PlanDetails };
    if (state && state.planDetails) {
      setPlanDetails(state.planDetails);
    } else {
      navigate("/subscription");
    }
  }, [location, navigate]);

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/auth/login", {
        state: {
          from: location.pathname,
          message: "Please log in to complete your subscription purchase",
        },
      });
    }
  }, [loading, isAuthenticated, navigate, location.pathname]);

  const handlePayment = async () => {
    if (!planDetails || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const amount = parseFloat(planDetails.price.replace("BHD ", ""));
      const response = await initiateVPayPayment(user.email, amount);

      if (response.success && response.data.success) {
        // Store the track_id and plan details for payment verification
        const trackId = response.data.track_id;
        // localStorage.setItem(PAYMENT_TRACK_ID_KEY, trackId);
        // localStorage.setItem(PAYMENT_PLAN_DETAILS_KEY, JSON.stringify({
        //   planTitle: planDetails.title,
        //   amount: amount,
        //   userId: user.email,
        //   timestamp: new Date().toISOString()
        // }));

        // Create subscription in the database
        try {
          const planType = PLAN_TYPE_MAP[planDetails.title];
          const billingCycle =
            BILLING_CYCLE_MAP[planDetails.period.toLowerCase()];

          if (!planType || !billingCycle) {
            throw new Error("Invalid plan type or billing cycle");
          }

          if (!user.store_id) {
            throw new Error(
              "Store ID not found. Please ensure your account is properly set up."
            );
          }

          const subscriptionResponse = await createSubscription({
            business_id: user.store_id,
            plan_type: planType,
            billing_cycle: billingCycle,
            payment_method: "CREDIT_CARD",
            auto_renew: true,
            track_id: trackId,
          });

          if (!subscriptionResponse.success) {
            throw new Error(
              subscriptionResponse.error || "Failed to create subscription"
            );
          }

          // Redirect to payment page
          window.location.href = response.data.payment_url;
        } catch (subscriptionError) {
          console.error("Error creating subscription:", subscriptionError);
          setError("Failed to create subscription. Please try again.");
          setIsLoading(false);
          return;
        }
      } else {
        setError("Payment initiation failed. Please try again.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Payment initiation failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Don't render anything if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  if (!planDetails) {
    return null;
  }

  const total = parseFloat(planDetails.price.replace("BHD ", ""));
  const includedFeatures = planDetails.features || [];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <SecureBadge>
        <Box className="badge-content">
          <MemoryIcon
            className="icon"
            sx={{
              transform: "rotate(180deg)",
              fontSize: "22px",
            }}
          />
          <span className="text">Highly Secured Encryption</span>
        </Box>
      </SecureBadge>

      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: "text.primary",
        }}
      >
        Subscription Checkout
      </Typography>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 8,
          "& .MuiStepLabel-root .Mui-completed": {
            color: "success.main",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "primary.main",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 4,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <StyledPaper elevation={0}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Payment Details
            </Typography>

            {/* Security Badges */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 3,
                mt: 1,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SecurityIcon color="primary" />
                <Typography variant="body2">256-bit SSL Encryption</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedUserIcon color="primary" />
                <Typography variant="body2">Verified by VenPays</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <ShieldIcon color="primary" />
                <Typography variant="body2">Secure Transaction</Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                textAlign: "center",
                py: 4,
                px: 3,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "center",
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                mb: 3,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: "text.secondary",
                  maxWidth: "80%",
                  mx: "auto",
                }}
              >
                You will be securely redirected to VenPays, our trusted payment
                processing partner, to complete your purchase with bank-grade
                security.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 3,
                  mb: 4,
                }}
              >
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
                  alt="Mastercard"
                  sx={{ height: 40 }}
                />
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg"
                  alt="Visa"
                  sx={{ height: 40 }}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={handlePayment}
                disabled={isLoading}
                startIcon={<LockIcon />}
                sx={{
                  minWidth: "240px",
                  height: "52px",
                  alignSelf: "center",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Pay ${total.toFixed(2)} BHD Securely`
                )}
              </Button>
            </Box>

            {/* Trust Indicators */}
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                align="center"
                gutterBottom
              >
                Protected by industry-leading security measures
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                display="block"
              >
                Your payment information is encrypted and securely processed by
                VenPays
              </Typography>
            </Box>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Subscription Summary
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Enterprise Suite
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  >
                    {planDetails?.price}/month
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Full access to all premium features and enterprise-grade tools
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Premium Features Included:
                </Typography>

                <Box component="ul" sx={{ pl: 2 }}>
                  {summaryFeatures.map((feature, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{ mb: 1 }}
                    >
                      ✓ {feature}
                    </Typography>
                  ))}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {total.toFixed(2)} BHD
                  </Typography>
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  By proceeding with the payment, you agree to our terms of
                  service and privacy policy
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubscriptionCheckoutPage;
